import React from 'react';
import { motion } from 'framer-motion';

const benefitsData = [
  {
    title: 'IEEE Xplore Digital Library',
    description: 'Acceso a millones de artículos científicos, revistas, conferencias y estándares técnicos globales. El ecosistema digital fundamental para la investigación.',
    logo: '/assets/beneficios/xplore.png',
    span: 'md:col-span-2 lg:col-span-2',
    bg: 'bg-gradient-to-br from-blue-50 to-white',
    border: 'border-blue-100 hover:border-blue-300',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE Learning Network',
    description: 'Cursos, certificaciones y recursos educativos diseñados por expertos de la industria.',
    logo: '/assets/beneficios/learning-network.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'Beneficios Google & Email IEEE',
    description: 'Identidad profesional con @ieee.org, 200GB de almacenamiento en Google Drive, y acceso preferencial al ecosistema premium y herramientas IA de Google.',
    icon: (
      <svg className="h-12 w-12 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
      </svg>
    ),
    span: 'md:col-span-2 lg:col-span-2',
    bg: 'bg-gradient-to-br from-cyan-50 to-white',
    border: 'border-cyan-100 hover:border-cyan-300',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE Spectrum',
    description: 'Noticias tecnológicas, tendencias de innovación y artículos de ingeniería global.',
    logo: '/assets/beneficios/spectrum.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE Collabratec',
    description: 'Plataforma digital para conectar, colaborar y compartir conocimientos globalmente.',
    logo: '/assets/beneficios/collabratec.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE JobSite',
    description: 'Descubre oportunidades laborales y posiciones de investigación en ingeniería mundialmente.',
    logo: '/assets/beneficios/job-site.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE DataPort',
    description: 'Repositorio global para acceder, compartir y analizar datasets científicos y recursos de IA.',
    logo: '/assets/beneficios/dataport.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'IEEE Author Center',
    description: 'Herramientas y guías expertas para publicar artículos científicos y técnicos con éxito.',
    logo: '/assets/beneficios/author-center.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'EPICS en IEEE',
    description: 'Proyectos de ingeniería enfocados en impacto social, servicio comunitario e innovación real.',
    logo: '/assets/beneficios/epics.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
  {
    title: 'Conferencias & Networking',
    description: 'Congresos internacionales, presentaciones y eventos tecnológicos para potenciar tu carrera y red de contactos.',
    logo: '/assets/beneficios/conferences-and-events.png',
    span: 'col-span-1',
    bg: 'bg-white hover:bg-slate-50',
    border: 'border-slate-100 hover:border-slate-200',
    shadow: 'hover:shadow-premium',
  },
];

const Benefits: React.FC = () => {
  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32" id="benefits">
      {/* Background ambient lighting - soft for light mode */}
      <div className="absolute top-40 left-0 w-96 h-96 rounded-full blur-[140px] bg-blue-100/40 pointer-events-none" />
      <div className="absolute bottom-40 right-0 w-80 h-80 rounded-full blur-[120px] bg-cyan-100/40 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-20 space-y-6"
        >
          <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm w-fit mx-auto">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-600 shadow-[0_0_8px_rgba(8,145,178,0.3)]" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">Ecosistema IEEE</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-tight max-w-4xl mx-auto">
            Herramientas y plataformas para tu{' '}
            <span className="text-cyan-600">
              crecimiento profesional
            </span>
          </h2>
          
          <p className="mx-auto max-w-2xl text-lg sm:text-xl text-slate-600 font-normal leading-relaxed">
            Accede a un entorno tecnológico premium diseñado para potenciar tu investigación, aprendizaje y conexiones globales.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[minmax(220px,auto)]">
          {benefitsData.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true, margin: '-50px' }}
              whileHover={{ y: -6 }}
              className={`group flex flex-col justify-between overflow-hidden rounded-[32px] border p-8 transition-all duration-500 ${benefit.bg} ${benefit.border} ${benefit.shadow} ${benefit.span}`}
            >
              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between mb-8">
                <div className="flex items-center justify-center h-16 w-auto max-w-[140px]">
                  {benefit.logo ? (
                    <img 
                      src={benefit.logo} 
                      alt={benefit.title} 
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105 mix-blend-multiply"
                    />
                  ) : (
                    benefit.icon
                  )}
                </div>
                
                {benefit.span.includes('col-span-2') && (
                  <div className="hidden sm:flex h-px w-24 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                )}
              </div>

              <div className="space-y-4 mt-auto">
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">{benefit.title}</h3>
                <p className="text-sm sm:text-base text-slate-500 font-normal leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Benefits;
